<template>
  <div class="about-page">
    <!-- Intro -->
    <section class="about-hero">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-7 col-md-7">
            <p class="eyebrow">About</p>
            <h1 class="hero-title">Meet Off The Couch.</h1>
            <p class="hero-sub">
              Off The Couch is the Venue Engine. The operations platform that runs
              a booked experience venue end to end, built by the operators we used to be.
            </p>
            <p class="hero-body">
              Off The Couch was founded by Jana and Nick Schilbe, who left careers
              in cybersecurity to open their own escape room in 2017. After several
              years of stitching commercial booking, waiver, and operations tools
              together, and finding none of them designed for the way an experience
              venue actually runs, we started building the engine we wished existed.
            </p>
            <p class="hero-body">
              Off The Couch entered beta in 2023 with input from more than 150
              venues and launched publicly in 2024. Today, escape rooms, axe throwing
              venues, VR centers, indoor mini golf, challenge arcades, haunted houses,
              adventure tours, and other booked experience venues across the US, UK,
              Europe, South Africa, and beyond run their day-to-day operations on the
              engine.
            </p>
          </div>
          <div class="col-lg-5 col-md-5">
            <img
              src="/img/pandas/wave.png"
              alt="Biku, the Off The Couch mascot, waving hello"
              class="hero-image"
              loading="eager"
              width="400"
              height="400"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Principles -->
    <section class="principles-section">
      <div class="container">
        <div class="row justify-content-center text-center mb-5">
          <div class="col-lg-8">
            <h2 class="section-title">What we believe</h2>
            <p class="section-sub">
              A few principles that show up everywhere in the product.
            </p>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4 mb-4" v-for="principle in principles" :key="principle.title">
            <div class="principle-card">
              <div class="principle-icon">
                <svg-icon :name="principle.icon" :size="22" />
              </div>
              <h3>{{ principle.title }}</h3>
              <p>{{ principle.body }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- End-of-page CTA band -->
    <section class="cta-band">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <h2 class="cta-band-title">Want to see how it fits your venue?</h2>
            <p class="cta-band-sub">
              We'll walk through the platform live and answer questions specific to
              your operation.
            </p>
            <div class="cta-band-buttons">
              <a href="/contact" class="btn-cta-primary">Get a demo</a>
              <a href="/features" class="btn-cta-secondary">Browse features &rarr;</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "AboutUs",
  data() {
    return {
      principles: [
        {
          icon: 'layers',
          title: 'One engine, not a stack',
          body: "Every feature shares a single data model. Bookings know about waivers, waivers know about customers, customers carry their full history with them. The integrations work because there are no integrations.",
        },
        {
          icon: 'dollar-sign',
          title: 'Pricing that respects you',
          body: "One plan, every feature included. A simple monthly minimum, then pay-per-use rates. We don't gate features, charge per seat, or surcharge per location.",
        },
        {
          icon: 'users',
          title: 'Operators talking to operators',
          body: "We use Off The Couch to run our own venues. Customer requests come straight to the people building the engine, and improvements ship as soon as they're ready.",
        },
        {
          icon: 'sparkles',
          title: 'Build for the work, not the demo',
          body: "Every feature has to earn its place by making a real day-to-day workflow easier. We measure success by what our customers do with the engine, not by feature counts.",
        },
        {
          icon: 'globe',
          title: 'Built with the community',
          body: "Our roadmap is shaped by the businesses using OTC every day. Customers share ideas, vote on features, request enhancements, and get support directly through our Discord, Facebook community, and team. By bringing 1,000+ locations onto one platform, we can solve problems together, build faster, and create technology that makes the entire industry stronger.",
        },
        {
          icon: 'shield',
          title: 'Your data stays your data',
          body: "We back our customers' data continuously and provide full export of any record, any time. The engine is designed to work for you, not to lock you in.",
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.about-page { padding-top: 110px; padding-bottom: 80px; }

.about-hero {
  position: relative;
  padding: 4rem 0 5rem;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -150px; left: -150px;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(225, 78, 202, 0.15), transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
  .container { position: relative; z-index: 1; }
}

.hero-sub {
  font-size: 1.2rem;
  color: white;
  font-weight: 500;
  line-height: 1.55;
  margin-bottom: 1.5rem;
}

.hero-body {
  color: $muted;
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 1rem;
}

.hero-image {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  filter: drop-shadow(0 25px 50px rgba(0, 0, 0, 0.4));
}

.principles-section { padding: 5rem 0; }

.principle-card {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 2rem;
  height: 100%;
  transition: border-color 0.2s ease, transform 0.2s ease;
  &:hover { border-color: $primary-border; transform: translateY(-2px); }
  h3 { color: white; font-size: 1.15rem; font-weight: 600; margin: 1rem 0 0.5rem; }
  p { color: $muted; font-size: 0.95rem; line-height: 1.55; margin: 0; }
}

.principle-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: $teal-soft;
  border: 1px solid rgba(46, 201, 183, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $teal;
}

@media (max-width: 768px) {
  .about-page { padding-top: 90px; }
  .hero-sub { font-size: 1.05rem; }
  .principles-section { padding: 3rem 0; }
  .hero-image {
    max-height: 220px;
    object-fit: contain;
    margin-top: 1.5rem;
  }
}
</style>
